import React, { useEffect } from 'react';
import BranchCard from '../BranchCard';
import { branches } from '../../data/branches';
import MapSection from '../MapSection';
import PageHero from '../PageHero';
import { Building, PhoneCall } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';

const Branches = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      {/* Attractive Hero Banner */}
      <PageHero
        badge={t('branchesSection.pageHeroBadge')}
        title={t('branchesSection.pageHeroTitle')}
        subtitle={t('branchesSection.pageHeroSubtitle')}
      />

      {/* Main Content */}
      <div className="container mx-auto px-4 lg:px-8 -mt-16 relative z-20 pb-24">
        
        {/* Stats Row */}
        <motion.div 
           initial={{ opacity: 0, y: 30 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.6, delay: 0.2 }}
           className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-16 flex flex-col md:flex-row justify-center gap-8 md:gap-24 hover:border-gray-300 transition-colors"
        >
           <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-white border border-gray-100 text-clinical rounded-full flex items-center justify-center shadow-sm">
                 <Building size={24} strokeWidth={1.5} />
              </div>
              <div>
                 <h4 className="text-navy font-bold text-xl">{t('branchesSection.statClinics')}</h4>
                 <p className="text-gray-500 text-sm">{t('branchesSection.statClinicsDesc')}</p>
              </div>
           </div>
           <div className="hidden md:block w-px h-14 bg-gray-200"></div>
           <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-white border border-gray-100 text-clinical rounded-full flex items-center justify-center shadow-sm">
                 <PhoneCall size={24} strokeWidth={1.5} />
              </div>
              <div>
                 <h4 className="text-navy font-bold text-xl">{t('branchesSection.statBooking')}</h4>
                 <p className="text-gray-500 text-sm">{t('branchesSection.statBookingDesc')}</p>
              </div>
           </div>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          {branches.map((branch, index) => (
            <BranchCard key={branch.slug} branch={branch} index={index} />
          ))}
        </div>
        
        {/* Map Section */}
        <motion.div 
           initial={{ opacity: 0, y: 40 }}
           whileInView={{ opacity: 1, y: 0 }}
           viewport={{ once: true }}
           transition={{ duration: 0.6 }}
           className="bg-white p-8 lg:p-12 rounded-sm border border-gray-200 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+PHBhdGggZD0iTTQwIDBMMCAwIDAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2YxZjVmOSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9zdmc+')] pointer-events-none opacity-40 z-0"></div>
          
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-4">
              <span className="w-8 h-[1px] bg-teal"></span>
              <span className="text-teal font-bold tracking-widest text-xs uppercase">{t('branchesSection.navigate')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-navy mb-8 tracking-tight">{t('branchesSection.findOnMap')}</h2>
            <div className="rounded-sm overflow-hidden border border-gray-200 bg-white p-1 shadow-sm h-[500px]">
               <MapSection />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Branches;
